import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from "react";
const PAYSTACK_FEE_NAIRA = 100;
export const PayoutMethodsManager = ({ baseURL, accessToken })=>{
    const [methods, setMethods] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [info, setInfo] = useState(null);
    const [showAdd, setShowAdd] = useState(false);
    const [actingId, setActingId] = useState(null);
    const authHeaders = useMemo(()=>({
            'Content-Type': 'application/json',
            ...accessToken ? {
                Authorization: `Bearer ${accessToken}`
            } : {}
        }), [
        accessToken
    ]);
    const refresh = useCallback(async ()=>{
        if (!accessToken) return;
        setLoading(true);
        setError(null);
        try {
            const res = await fetch(`${baseURL}/payments/payout-methods`, {
                headers: authHeaders
            });
            if (!res.ok) throw await asError(res);
            setMethods(await res.json());
        } catch (err) {
            setError(extractMessage(err));
        } finally{
            setLoading(false);
        }
    }, [
        authHeaders,
        baseURL,
        accessToken
    ]);
    useEffect(()=>{
        refresh();
    }, [
        refresh
    ]);
    const setDefault = async (m)=>{
        setActingId(m.id);
        setError(null);
        setInfo(null);
        try {
            const res = await fetch(`${baseURL}/payments/payout-methods/${m.id}/set-default`, {
                method: 'POST',
                headers: authHeaders
            });
            if (!res.ok) throw await asError(res);
            setInfo('Default payout method updated.');
            await refresh();
        } catch (err) {
            setError(extractMessage(err));
        } finally{
            setActingId(null);
        }
    };
    const remove = async (m)=>{
        if (!window.confirm('Remove this bank account?')) return;
        setActingId(m.id);
        setError(null);
        setInfo(null);
        try {
            const res = await fetch(`${baseURL}/payments/payout-methods/${m.id}`, {
                method: 'DELETE',
                headers: authHeaders
            });
            if (!res.ok) throw await asError(res);
            setInfo('Bank account removed.');
            await refresh();
        } catch (err) {
            setError(extractMessage(err));
        } finally{
            setActingId(null);
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-base font-semibold text-gray-900 dark:text-white",
                        children: "Payout methods"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-xs text-gray-500 dark:text-gray-400",
                        children: "Choose where your earnings settle. Swiftmedix Wallet is always available as a free fallback."
                    })
                ]
            }),
            error && /*#__PURE__*/ _jsx("div", {
                className: "rounded-md border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700",
                children: error
            }),
            info && /*#__PURE__*/ _jsx("div", {
                className: "rounded-md border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-700",
                children: info
            }),
            loading ? /*#__PURE__*/ _jsx("div", {
                className: "text-sm text-gray-500",
                children: "Loading…"
            }) : /*#__PURE__*/ _jsx("ul", {
                className: "space-y-2",
                children: methods.map((m)=>/*#__PURE__*/ _jsx("li", {
                        className: 'rounded-lg border p-3 ' + (m.is_default ? 'border-primary bg-blue-50/40 dark:bg-blue-950/30' : 'border-gray-200 dark:border-gray-800'),
                        children: /*#__PURE__*/ _jsxs("div", {
                            className: "flex items-start justify-between gap-3",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "min-w-0",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2 flex-wrap",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium text-sm text-gray-900 dark:text-white",
                                                    children: labelFor(m)
                                                }),
                                                m.is_default && /*#__PURE__*/ _jsx("span", {
                                                    className: "rounded bg-primary px-1.5 py-0.5 text-[10px] font-semibold uppercase text-white",
                                                    children: "Default"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-xs text-gray-500 dark:text-gray-400",
                                                    children: feeFor(m.type)
                                                })
                                            ]
                                        }),
                                        m.verified_account_name && /*#__PURE__*/ _jsx("div", {
                                            className: "mt-0.5 text-xs text-gray-600 dark:text-gray-400 truncate",
                                            children: m.verified_account_name
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex shrink-0 gap-2",
                                    children: [
                                        !m.is_default && /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            disabled: actingId === m.id,
                                            onClick: ()=>setDefault(m),
                                            className: "text-xs font-medium text-primary hover:underline disabled:opacity-50",
                                            children: "Set as default"
                                        }),
                                        m.type !== 'embedly_wallet' && /*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            disabled: actingId === m.id,
                                            onClick: ()=>remove(m),
                                            className: "text-xs text-red-600 hover:underline disabled:opacity-50",
                                            children: "Remove"
                                        })
                                    ]
                                })
                            ]
                        })
                    }, m.id))
            }),
            /*#__PURE__*/ _jsx("div", {
                children: /*#__PURE__*/ _jsx("button", {
                    type: "button",
                    onClick: ()=>{
                        setShowAdd(true);
                        setError(null);
                        setInfo(null);
                    },
                    className: "w-full sm:w-auto px-4 py-2 text-sm font-medium rounded-lg border border-dashed border-primary text-primary hover:bg-blue-50 dark:hover:bg-blue-950",
                    children: "+ Add bank account"
                })
            }),
            showAdd && /*#__PURE__*/ _jsx(AddBankModal, {
                baseURL: baseURL,
                authHeaders: authHeaders,
                onClose: ()=>setShowAdd(false),
                onAdded: ()=>{
                    setShowAdd(false);
                    setInfo('Bank account added.');
                    refresh();
                }
            })
        ]
    });
};
export default PayoutMethodsManager;
export function AddBankModal({ baseURL, authHeaders, onClose, onAdded }) {
    const [banks, setBanks] = useState([]);
    const [bankCode, setBankCode] = useState('');
    const [bankName, setBankName] = useState('');
    const [accountNumber, setAccountNumber] = useState('');
    const [provider, setProvider] = useState('embedly');
    const [resolvedName, setResolvedName] = useState(null);
    const [resolveOk, setResolveOk] = useState(null);
    const [resolveMsg, setResolveMsg] = useState(null);
    const [resolving, setResolving] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    // Bank list is provider-specific — Embedly and Paystack maintain separate
    // bank codes that are NOT interchangeable. Refetch whenever the provider
    // changes, and clear any bank/resolve state selected under the old list.
    useEffect(()=>{
        setBanks([]);
        setBankCode('');
        setBankName('');
        setResolvedName(null);
        setResolveOk(null);
        setResolveMsg(null);
        (async ()=>{
            try {
                const res = await fetch(`${baseURL}/payments/payout-methods/banks?provider=${provider}`, {
                    headers: authHeaders
                });
                if (!res.ok) throw await asError(res);
                const data = await res.json();
                const list = data?.data ?? data ?? [];
                // Embedly's bank list occasionally repeats the same code under a
                // different name (observed for 057) — keep the first occurrence so
                // <option key={code}> stays unique and no bank silently disappears.
                const seen = new Set();
                const deduped = list.filter((b)=>{
                    if (!b.code || seen.has(b.code)) return false;
                    seen.add(b.code);
                    return true;
                });
                setBanks(deduped.sort((a, b)=>(a.name ?? '').localeCompare(b.name ?? '')));
            } catch (err) {
                setError(extractMessage(err));
            }
        })();
    }, [
        authHeaders,
        baseURL,
        provider
    ]);
    const onBankChange = (code)=>{
        setBankCode(code);
        setBankName(banks.find((b)=>b.code === code)?.name ?? '');
        setResolvedName(null);
        setResolveOk(null);
        setResolveMsg(null);
    };
    const onAccountNumberChange = (n)=>{
        setAccountNumber(n);
        setResolvedName(null);
        setResolveOk(null);
        setResolveMsg(null);
    };
    const verify = async ()=>{
        if (!bankCode || accountNumber.length < 10) return;
        setResolving(true);
        setError(null);
        try {
            const res = await fetch(`${baseURL}/payments/payout-methods/resolve`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    bank_code: bankCode,
                    account_number: accountNumber,
                    provider
                })
            });
            if (!res.ok) throw await asError(res);
            const data = await res.json();
            setResolvedName(data?.resolved_account_name ?? '');
            setResolveOk(!!data?.match_ok);
            setResolveMsg(data?.match_reason ?? null);
        } catch (err) {
            setError(extractMessage(err));
        } finally{
            setResolving(false);
        }
    };
    // Auto-verify when 10 digits typed.
    useEffect(()=>{
        if (bankCode && accountNumber.length === 10) {
            verify();
        }
    }, [
        bankCode,
        accountNumber
    ]);
    const canSave = resolveOk === true && !saving;
    const save = async ()=>{
        if (!canSave) return;
        setSaving(true);
        setError(null);
        try {
            const res = await fetch(`${baseURL}/payments/payout-methods`, {
                method: 'POST',
                headers: authHeaders,
                body: JSON.stringify({
                    bank_code: bankCode,
                    bank_name: bankName,
                    account_number: accountNumber,
                    provider
                })
            });
            if (!res.ok) throw await asError(res);
            const created = await res.json().catch(()=>undefined);
            onAdded(created);
        } catch (err) {
            setError(extractMessage(err));
        } finally{
            setSaving(false);
        }
    };
    return /*#__PURE__*/ _jsx("div", {
        className: "fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-center bg-black/55 backdrop-blur-sm p-0 sm:p-4",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full sm:max-w-md max-h-[88vh] sm:max-h-[80vh] overflow-y-auto bg-white dark:bg-[#111] rounded-t-2xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-800",
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 pt-5 pb-3 border-b border-gray-200 dark:border-gray-800 flex items-start justify-between gap-3",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            children: [
                                /*#__PURE__*/ _jsx("h2", {
                                    className: "font-semibold text-base sm:text-lg text-gray-900 dark:text-white",
                                    children: "Add bank account"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-0.5 text-xs text-gray-500 dark:text-gray-400",
                                    children: "We’ll verify the account name matches your Swiftmedix profile."
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: onClose,
                            className: "text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white text-2xl leading-none p-1 -m-1",
                            "aria-label": "Close",
                            children: "×"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 py-4 space-y-3",
                    children: [
                        /*#__PURE__*/ _jsxs("fieldset", {
                            className: "block text-xs font-medium text-gray-700 dark:text-gray-300",
                            children: [
                                /*#__PURE__*/ _jsx("legend", {
                                    className: "mb-1",
                                    children: "Payout provider"
                                }),
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "flex items-start gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 cursor-pointer mb-2",
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "radio",
                                            name: "provider",
                                            value: "embedly",
                                            checked: provider === 'embedly',
                                            onChange: ()=>setProvider('embedly'),
                                            className: "mt-0.5"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium text-gray-900 dark:text-white",
                                                    children: "Via Swiftmedix (Embedly)"
                                                }),
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "block text-gray-500 dark:text-gray-400 mt-0.5",
                                                    children: "Free for you. Subject to platform balance availability."
                                                })
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("label", {
                                    className: "flex items-start gap-2 rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 cursor-pointer",
                                    children: [
                                        /*#__PURE__*/ _jsx("input", {
                                            type: "radio",
                                            name: "provider",
                                            value: "paystack",
                                            checked: provider === 'paystack',
                                            onChange: ()=>setProvider('paystack'),
                                            className: "mt-0.5"
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium text-gray-900 dark:text-white",
                                                    children: "Via Paystack"
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "block text-gray-500 dark:text-gray-400 mt-0.5",
                                                    children: [
                                                        "Always available. ",
                                                        /*#__PURE__*/ _jsxs("strong", {
                                                            children: [
                                                                "₦",
                                                                PAYSTACK_FEE_NAIRA
                                                            ]
                                                        }),
                                                        " per payout, deducted from your settlement."
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("label", {
                            className: "block text-xs font-medium text-gray-700 dark:text-gray-300",
                            children: [
                                "Bank",
                                /*#__PURE__*/ _jsxs("select", {
                                    value: bankCode,
                                    onChange: (e)=>onBankChange(e.target.value),
                                    disabled: banks.length === 0,
                                    className: "mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-2 text-sm text-gray-900 dark:text-white disabled:opacity-60",
                                    children: [
                                        /*#__PURE__*/ _jsx("option", {
                                            value: "",
                                            children: banks.length === 0 ? 'Loading banks…' : 'Select bank…'
                                        }),
                                        banks.map((b)=>/*#__PURE__*/ _jsx("option", {
                                                value: b.code,
                                                children: b.name
                                            }, b.code))
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("label", {
                            className: "block text-xs font-medium text-gray-700 dark:text-gray-300",
                            children: [
                                "Account number",
                                /*#__PURE__*/ _jsx("input", {
                                    type: "text",
                                    inputMode: "numeric",
                                    maxLength: 10,
                                    value: accountNumber,
                                    onChange: (e)=>onAccountNumberChange(e.target.value.replace(/\D/g, '')),
                                    className: "mt-1 w-full rounded border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 px-2 py-2 text-sm text-gray-900 dark:text-white tracking-widest",
                                    placeholder: "10 digits"
                                })
                            ]
                        }),
                        (resolving || resolvedName || resolveMsg) && /*#__PURE__*/ _jsxs("div", {
                            className: 'rounded-md px-3 py-2 text-xs ' + (resolveOk === true ? 'bg-emerald-50 text-emerald-800 border border-emerald-200' : resolveOk === false ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-gray-50 text-gray-600 border border-gray-200'),
                            children: [
                                resolving && 'Verifying…',
                                !resolving && resolveOk === true && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium",
                                            children: "Verified:"
                                        }),
                                        " ",
                                        resolvedName
                                    ]
                                }),
                                !resolving && resolveOk === false && /*#__PURE__*/ _jsx(_Fragment, {
                                    children: resolveMsg ?? 'Name does not match your profile.'
                                })
                            ]
                        }),
                        error && /*#__PURE__*/ _jsx("div", {
                            className: "rounded-md border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700",
                            children: error
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "px-5 py-3 border-t border-gray-200 dark:border-gray-800 flex flex-col-reverse sm:flex-row gap-2 sm:justify-end",
                    children: [
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: onClose,
                            disabled: saving,
                            className: "px-4 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50",
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx("button", {
                            type: "button",
                            onClick: save,
                            disabled: !canSave,
                            className: "px-4 py-2 text-sm font-semibold rounded-lg bg-primary text-white hover:opacity-90 disabled:opacity-50",
                            children: saving ? 'Saving…' : 'Save bank account'
                        })
                    ]
                })
            ]
        })
    });
}
// ---------------------- helpers ----------------------
function labelFor(m) {
    if (m.type === 'embedly_wallet') return 'Swiftmedix Wallet';
    const masked = m.account_number ? `****${m.account_number.slice(-4)}` : '';
    const via = m.provider === 'embedly' ? 'via Embedly' : 'via Paystack';
    return `${m.bank_name ?? 'Bank'} ${masked}  ·  ${via}`;
}
function feeFor(t) {
    if (t === 'paystack_bank') return '₦100 per payout';
    return 'Free';
}
async function asError(res) {
    let msg = `Request failed (${res.status})`;
    try {
        const j = await res.json();
        if (j?.message) msg = j.message;
    } catch  {
    /* ignore */ }
    return new Error(msg);
}
function extractMessage(err) {
    if (err instanceof Error) return err.message;
    if (typeof err === 'string') return err;
    return 'Something went wrong';
}

//# sourceMappingURL=PayoutMethodsManager.js.map